/**
 * useOrderSubscription
 *
 * Subscribes to the POS real-time AppSync API for new order notifications.
 * When a new order arrives (from Uber Eats webhook or any source), it is
 * prepended to the Redux transaction state and a toast notification is shown.
 *
 * Usage: mount this hook once at the app level (Admin layout) when a shop is selected.
 */
import { useEffect, useRef } from 'react';
import { API } from '@aws-amplify/api';
import { Amplify } from '@aws-amplify/core';
import Swal from 'sweetalert2';
import awsconfig from '../internals/config/aws_config';
import Actions from '../modules/transaction/Actions';

// Inline dispatch type to avoid circular dependency
type DispatchFn = (action: any) => void;

const ON_NEW_ORDER = /* GraphQL */ `
  subscription OnNewOrder($shopId: String!) {
    onNewOrder(shopId: $shopId) {
      shopId
      orderId
      source
      orderNumber
      total
      status
      createdAt
      uberEatsDisplayId
      uberEatsOrderType
      customerName
      deliveryAddress
      uberEatsMeta
    }
  }
`;

function showOrderToast(order: any) {
  const isUberEats = order.source === 'ubereats';
  const title      = isUberEats
    ? `🛵 New Uber Eats Order #${order.uberEatsDisplayId || order.orderId}`
    : `🆕 New Order ${order.orderNumber || order.orderId}`;
  const text = [
    order.customerName ? `Customer: ${order.customerName}` : null,
    order.total != null ? `Total: $${Number(order.total).toFixed(2)}` : null,
    order.uberEatsOrderType === 'DELIVERY_ORDER' ? '📦 Delivery' :
    order.uberEatsOrderType === 'PICKUP_ORDER'   ? '🏃 Pickup'   : null,
  ].filter(Boolean).join('  ·  ');

  Swal.fire({
    toast:             true,
    position:          'top-end',
    icon:              'info',
    title,
    text:              text || undefined,
    showConfirmButton: false,
    timer:             8000,
    timerProgressBar:  true,
    background:        isUberEats ? '#ebfff5' : undefined,
  });
}

export function useOrderSubscription(shopId: string | undefined, dispatch: DispatchFn) {
  const subRef = useRef<any>(null);

  useEffect(() => {
    if (!shopId || !(awsconfig as any).pos_realtime_appsync_endpoint) return;

    // Temporarily point Amplify at the POS real-time AppSync
    // The subscription captures the WebSocket immediately; we restore the
    // original config right after so payment/other calls are unaffected.
    const posEndpoint = (awsconfig as any).pos_realtime_appsync_endpoint;

    Amplify.configure({
      aws_appsync_graphqlEndpoint:    posEndpoint,
      aws_appsync_region:             awsconfig.aws_appsync_region || 'ap-southeast-1',
      aws_appsync_authenticationType: 'AWS_IAM',
    });

    try {
      subRef.current = (API.graphql({
        query:     ON_NEW_ORDER,
        variables: { shopId },
      }) as any).subscribe({
        next: ({ value }: any) => {
          const order = value?.data?.onNewOrder;
          if (!order) return;
          console.log('[useOrderSubscription] New order received:', order.orderId);
          dispatch(Actions.realtimeNewOrder(order));
          showOrderToast(order);
        },
        error: (err: any) => {
          console.error('[useOrderSubscription] Subscription error:', err);
        },
      });
    } catch (err) {
      console.error('[useOrderSubscription] Failed to subscribe:', err);
    }

    // Restore the original Amplify config so payment GraphQL calls still work
    Amplify.configure({
      aws_appsync_graphqlEndpoint:    awsconfig.aws_appsync_graphqlEndpoint,
      aws_appsync_region:             awsconfig.aws_appsync_region,
      aws_appsync_authenticationType: awsconfig.aws_appsync_authenticationType,
    });

    return () => {
      if (subRef.current) {
        try { subRef.current.unsubscribe(); } catch { /* noop */ }
        subRef.current = null;
      }
    };
  }, [shopId, dispatch]);
}
